'use client'
import { useReducer, useMemo, useCallback, useRef, useEffect } from 'react'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'

import { SessionData } from '@/app/adapters/types'
import { extractApiErrorDetails } from '@lib/errors/apiError'
import { ExpApiErrorCode } from '@lib/errors/models/errorTypes'
import { createInitialState, createTableReducer, TableState, createSortParam } from '@utilities/tableUtils'
import { getListingsData, getTransactionDetails, TransactionDetailsQueryParams } from '@service/experienceService'
import { ListingsApiData, TransactionsApiData } from '@service/models/biListings'
import { AppConstants } from '@/lib/constants/constants'
import { getStatusValues } from '@utilities/dataUtils'
import { getDateStr, getLast30DaysDateRange } from '@utilities/dateFormatter'
import { getUtcDateVals } from '@utilities/performanceMetricsUtils'
import { Strings } from '@/lib/constants/strings'
import { PartyTypeCd } from '@/lib/constants/permissions'
import { useCompanyContext } from '@/lib/context/companyContext'
import { useOfficeListContext } from '@/lib/context/biContext/OfficeListContext'
import {
	Period,
	SegmentType,
	DateRangeCd,
	TransactionStatusCd,
	TransactionSortCd,
	ListingsSortCd,
} from '../enums/bi-enums'

interface BaseQueryParams {
	partyId: string | undefined
	partyTypeCd: string
	listingStatusCds: string
	pageSz: number
	pageNo: number
}

export type DataSource =
	| 'listings'
	| 'offMarketListings'
	| 'dealPipelineTransactions'
	| 'pastEstimatedCloseTransactions'

interface UseTableDataOptions {
	dataSource: DataSource
	errorMessage: string
	sessionData: SessionData
	activeSegmentIndex?: number
	initialState?: TableState<ListingsApiData | TransactionsApiData>
	defaultSortField?: string
	defaultSortDirection?: 'asc' | 'desc'
	selectedOfficeId?: string | null
}

// Simple function to create sort parameter from GridSortModel
const createSortFromModel = (
	sortModel: GridSortModel,
	dataSource: DataSource,
	defaultField?: string,
	defaultDirection?: 'asc' | 'desc'
): TransactionSortCd | ListingsSortCd => {
	const isListingsTable = dataSource === 'listings' || dataSource === 'offMarketListings'

	if (!sortModel || sortModel.length === 0) {
		// Return default sort if provided
		if (defaultField && defaultDirection) {
			const sortParam = createSortParam(defaultField, defaultDirection)
			if (isListingsTable) {
				return sortParam as ListingsSortCd
			}
			return sortParam as TransactionSortCd
		}
		// Default fallback
		if (isListingsTable) {
			return ListingsSortCd.LISTED_DATE_DESC
		}
		return TransactionSortCd.CONTRACT_DATE_DESC
	}

	const sortItem = sortModel[0]
	const sortParam = createSortParam(sortItem.field, sortItem.sort as 'asc' | 'desc')
	if (isListingsTable) {
		return sortParam as ListingsSortCd
	}
	return sortParam as TransactionSortCd
}

export const useTableData = ({
	dataSource,
	errorMessage,
	sessionData,
	activeSegmentIndex,
	initialState,
	defaultSortField,
	defaultSortDirection,
}: UseTableDataOptions) => {
	const [state, dispatch] = useReducer(
		createTableReducer<ListingsApiData | TransactionsApiData>(),
		initialState ?? createInitialState<ListingsApiData | TransactionsApiData>()
	)
	const { selectedCompanyId } = useCompanyContext()
	const { selectedOffice } = useOfficeListContext()

	const lastStatus = useRef<string | null>(state.selectedStatus)

	// Initialize default sort model if none exists
	useEffect(() => {
		if (state.sortModel.length === 0 && defaultSortField && defaultSortDirection) {
			dispatch({
				type: 'SET_SORT_MODEL',
				payload: [{ field: defaultSortField, sort: defaultSortDirection }],
			})
		}
	}, [defaultSortField, defaultSortDirection, state.sortModel.length])

	// Helper function to handle errors with reduced complexity
	const handleError = useCallback((error: unknown, fallbackMessage: string) => {
		console.error(fallbackMessage, error)
		dispatch({ type: 'SET_DATA', payload: null })

		const errorDetails = extractApiErrorDetails(error, fallbackMessage)
		const errorCd = 'errorCd' in errorDetails ? errorDetails.errorCd : undefined

		const isSourceNotFoundError = errorCd === ExpApiErrorCode.SourceNotFoundError
		const finalErrorMessage = isSourceNotFoundError
			? ExpApiErrorCode.SourceNotFoundError
			: (fallbackMessage ?? Strings.SOMETHING_WENT_WRONG)

		dispatch({
			type: 'SET_ERROR',
			payload: {
				error: true,
				errorMessage: finalErrorMessage,
			},
		})
	}, [])

	const actions = useMemo(
		() => ({
			setData: (data: ListingsApiData | TransactionsApiData | null) => {
				dispatch({ type: 'SET_DATA', payload: data })
			},
			setLoading: (loading: boolean) => {
				dispatch({ type: 'SET_LOADING', payload: loading })
			},
			setSelectedStatus: (status: string | null) => {
				if (lastStatus.current !== status) {
					lastStatus.current = status
					dispatch({ type: 'SET_SELECTED_STATUS', payload: status })
				}
			},
			setRowsPerPage: (rowsPerPage: number) => {
				dispatch({ type: 'SET_ROWS_PER_PAGE', payload: rowsPerPage })
			},
			setPaginationModel: (model: GridPaginationModel) => {
				dispatch({ type: 'SET_PAGINATION_MODEL', payload: model })
			},
			setSortModel: (model: GridSortModel) => {
				dispatch({ type: 'SET_SORT_MODEL', payload: model })
			},
			handleSortModelChange: (model: GridSortModel) => {
				dispatch({
					type: 'SET_SORT_MODEL',
					payload: model.length === 0 ? [] : model,
				})
				// Reset pagination to page 1 when sort changes
				dispatch({
					type: 'SET_PAGINATION_MODEL',
					payload: { page: 1, pageSize: state.paginationModel.pageSize },
				})
			},
			setTotalCount: (count: number) => {
				dispatch({ type: 'SET_TOTAL_COUNT', payload: count })
			},
			setError: (error: boolean, message?: string) => {
				dispatch({
					type: 'SET_ERROR',
					payload: { error, errorMessage: message },
				})
			},
		}),
		[]
	)

	// Helper function to build party details
	const buildPartyDetails = useCallback(() => {
		const partyDetails: { partyId: string; partyTypeCd: string } = {
			partyId: selectedCompanyId ?? '',
			partyTypeCd: PartyTypeCd.BROKER,
		}
		if (selectedOffice?.officeId && selectedOffice?.officeId !== selectedCompanyId) {
			partyDetails.partyId = selectedOffice?.officeId
			partyDetails.partyTypeCd = PartyTypeCd.OFFICE
		}
		return partyDetails
	}, [selectedCompanyId, selectedOffice?.officeId])

	// Helper function to build base query parameters
	const buildBaseQueryParams = useCallback(
		(partyDetails: { partyId: string; partyTypeCd: string }) => {
			return {
				partyId: partyDetails.partyId?.toLocaleUpperCase(),
				partyTypeCd: partyDetails.partyTypeCd,
				listingStatusCds: getStatusValues(AppConstants.ON_MARKET_STATUS_ITEMS, state.selectedStatus ?? '').join(','), //state.selectedStatus ?? undefined,
				pageSz: state.paginationModel.pageSize ?? 10,
				pageNo: state.paginationModel.page,
			}
		},
		[state.selectedStatus, state.paginationModel.pageSize, state.paginationModel.page]
	)

	// Helper function to get transaction status codes
	const getTransactionStatusCodes = useCallback((selectedStatus: string | null): string => {
		if (selectedStatus === 'All') {
			return `${TransactionStatusCd.PENDING},${TransactionStatusCd.CLOSED}`
		}
		if (selectedStatus === 'Closed') {
			return TransactionStatusCd.CLOSED
		}
		return TransactionStatusCd.PENDING
	}, [])

	// Helper function to get date range based on segment
	const getDateRangeForSegment = useCallback(() => {
		if (activeSegmentIndex === SegmentType.YTD) {
			const ytdVals = getUtcDateVals(Period.YTD)
			const ytdFromDt = new Date(Date.UTC(ytdVals.currentYear, 0, 1))
			const ytdToDt = new Date(Date.UTC(ytdVals.currentYear, ytdVals.currentMonth + 1, 0))
			return {
				fromDt: getDateStr(ytdFromDt),
				toDt: getDateStr(ytdToDt),
			}
		}
		const ltmVals = getUtcDateVals(Period.LTM)
		const ltmFromDt = new Date(Date.UTC(ltmVals.startYear, ltmVals.startMonth, 1))
		const ltmToDt = new Date(Date.UTC(ltmVals.currentYear, ltmVals.currentMonth, 0))
		return {
			fromDt: getDateStr(ltmFromDt),
			toDt: getDateStr(ltmToDt),
		}
	}, [activeSegmentIndex])

	// Fetch listings data
	const fetchListingsData = useCallback(
		async (queryParams: BaseQueryParams) => {
			const dateRange = getDateRangeForSegment()
			const listingsQueryParams = {
				...queryParams,
				...dateRange,
				dateRangeCd: DateRangeCd.LISTED,
				sortCd: createSortFromModel(state.sortModel, dataSource, defaultSortField, defaultSortDirection),
			}

			return getListingsData(listingsQueryParams, sessionData?.accessToken)
		},
		[
			getDateRangeForSegment,
			state.selectedStatus,
			state.sortModel,
			dataSource,
			defaultSortField,
			defaultSortDirection,
			sessionData?.accessToken,
		]
	)

	// Fetch off-market listings data
	const fetchOffMarketListingsData = useCallback(
		async (queryParams: BaseQueryParams, overrideStatus?: string) => {
			const { fromDt: last30DaysFrom, toDt: last30DaysTo } =
				getLast30DaysDateRange()
			const offMarketQueryParams = {
				...queryParams,
				fromDt: getDateStr(last30DaysFrom),
				toDt: getDateStr(last30DaysTo),
				dateRangeCd: DateRangeCd.OFF_MARKET,
				listingStatusCds: getStatusValues(
					AppConstants.OFF_MARKET_STATUS_ITEMS,
					overrideStatus ?? state.selectedStatus ?? ''
				).join(','),
				sortCd: createSortFromModel(state.sortModel, dataSource, defaultSortField, defaultSortDirection),
			}
			const apiData = await getListingsData(offMarketQueryParams, sessionData?.accessToken)

			if (state.selectedStatus === 'All' || overrideStatus === 'All') {
				dispatch({
					type: 'SET_TOTAL_COUNT',
					payload: apiData?.paginationData?.totalItemCt ?? 0,
				})
			}
			return apiData
		},
		[
			state.selectedStatus,
			state.sortModel,
			dataSource,
			defaultSortField,
			defaultSortDirection,
			sessionData?.accessToken,
		]
	)

	// Fetch deal pipeline transactions data
	const fetchDealPipelineTransactionsData = useCallback(async () => {
		const dateRange = getDateRangeForSegment()
		const dealPipelineQueryParams: TransactionDetailsQueryParams = {
			partyId: selectedCompanyId?.toLocaleUpperCase() ?? '',
			partyTypeCd: PartyTypeCd.BROKER,
			fromDt: dateRange.fromDt ?? '',
			toDt: dateRange.toDt ?? '',
			transactionStatusCds: getTransactionStatusCodes(state.selectedStatus),
			pageNo: state.paginationModel.page,
			pageSz: state.paginationModel.pageSize ?? 10,
			sortCd: createSortFromModel(
				state.sortModel,
				dataSource,
				defaultSortField,
				defaultSortDirection
			) as TransactionSortCd,
			dateRangeCd: DateRangeCd.CONTRACT,
		}

		if (selectedOffice?.officeId && selectedOffice?.officeId !== selectedCompanyId) {
			dealPipelineQueryParams.partyId = selectedOffice?.officeId
			dealPipelineQueryParams.partyTypeCd = PartyTypeCd.OFFICE
		}

		return getTransactionDetails(dealPipelineQueryParams, sessionData?.accessToken)
	}, [
		getDateRangeForSegment,
		selectedCompanyId,
		getTransactionStatusCodes,
		state.selectedStatus,
		state.paginationModel.page,
		state.paginationModel.pageSize,
		state.sortModel,
		dataSource,
		defaultSortField,
		defaultSortDirection,
		selectedOffice?.officeId,
		sessionData?.accessToken,
	])

	// Fetch past estimated close transactions data
	const fetchPastEstimatedTransactionsData = useCallback(async () => {
		const { fromDt: last30DaysFrom, toDt: last30DaysTo } = getLast30DaysDateRange()
		const pastEstimatedQueryParams: TransactionDetailsQueryParams = {
			partyId: selectedCompanyId?.toLocaleUpperCase() ?? '',
			partyTypeCd: PartyTypeCd.BROKER,
			fromDt: getDateStr(last30DaysFrom) ?? '',
			toDt: getDateStr(last30DaysTo) ?? '',
			pageNo: state.paginationModel.page,
			pageSz: state.paginationModel.pageSize ?? 10,
			sortCd: createSortFromModel(
				state.sortModel,
				dataSource,
				defaultSortField,
				defaultSortDirection
			) as TransactionSortCd,
			dateRangeCd: DateRangeCd.ESTIMATED_CLOSE,
		}
		if (selectedOffice?.officeId && selectedOffice?.officeId !== selectedCompanyId) {
			pastEstimatedQueryParams.partyId = selectedOffice?.officeId
			pastEstimatedQueryParams.partyTypeCd = PartyTypeCd.OFFICE
		}
		const apiData = await getTransactionDetails(pastEstimatedQueryParams, sessionData?.accessToken)
		dispatch({
			type: 'SET_TOTAL_COUNT',
			payload: apiData?.paginationData?.totalItemCt ?? 0,
		})
		return apiData
	}, [
		selectedCompanyId,
		state.paginationModel.page,
		state.paginationModel.pageSize,
		state.sortModel,
		dataSource,
		defaultSortField,
		defaultSortDirection,
		selectedOffice?.officeId,
		sessionData?.accessToken,
	])

	const fetchData = useCallback(async () => {
		try {
			// GUARD: Don't make API calls until companyId and accessToken are available
			if (!selectedCompanyId || !sessionData?.accessToken) {
				return
			}

			dispatch({ type: 'SET_LOADING', payload: true })
			const partyDetails = buildPartyDetails()
			const queryParams = buildBaseQueryParams(partyDetails)

			let apiData: ListingsApiData | TransactionsApiData | null = null

			switch (dataSource) {
				case 'listings':
					apiData = await fetchListingsData(queryParams)
					break
				case 'offMarketListings': {
					apiData = await fetchOffMarketListingsData(queryParams)
					// To manage banner count in case of different listing statuses
					if (state.selectedStatus !== 'All') await fetchOffMarketListingsData(queryParams, 'All')

					break
				}
				case 'dealPipelineTransactions':
					apiData = await fetchDealPipelineTransactionsData()
					break
				case 'pastEstimatedCloseTransactions':
					apiData = await fetchPastEstimatedTransactionsData()
					break
				default:
					break
			}

			if (apiData) {
				dispatch({
					type: 'SET_DATA',
					payload: apiData,
				})
			} else {
				dispatch({ type: 'SET_DATA', payload: null })
			}
		} catch (error) {
			handleError(error, errorMessage)
			dispatch({ type: 'SET_TOTAL_COUNT', payload: 0 })
		} finally {
			dispatch({ type: 'SET_LOADING', payload: false })
		}
	}, [
		dataSource,
		errorMessage,
		selectedCompanyId,
		sessionData?.accessToken,
		buildPartyDetails,
		buildBaseQueryParams,
		fetchListingsData,
		fetchOffMarketListingsData,
		fetchDealPipelineTransactionsData,
		fetchPastEstimatedTransactionsData,
		handleError,
	])

	return {
		state,
		actions,
		fetchData,
	}
}
